// src/prototype/components/execute/ExecSummaryTab.tsx
// Execute hub · Summary tab: where this initiative stands and what has to happen next.
// Shows dimension scores (never weights), the value and payback ranges, top risks and the
// next milestones. Percent Complete appears only when the client uploaded it (E2).
import React from 'react';
import { ArrowRight, Calendar, Shield, Target, Award, Flag } from 'lucide-react';
import { SectionCard } from '../SectionCard';
import { VerdictPill } from '../VerdictPill';
import { DIM_META } from '../../data/adapters';
import type { ScoresVM } from '../../data/adapters';
import type { DecisionBrief } from '../../data/decisionBrief';
import type { InitiativeRecord, ExecMilestone } from '../../data/executionInitiatives';
import { SEVERITY_DOT } from '../../theme/decisionTokens';
import { ActivityLog } from './ExecApprovalTab';
import type { ActivityEvent } from '../../hooks/useExecuteHubState';

export interface ExecSummaryTabProps {
  brief: DecisionBrief;
  record: InitiativeRecord;
  scores: ScoresVM;
  milestones: ExecMilestone[];
  approvalState: string;
  nextAction: { text: string; tab: 'approval' | 'execution' };
  percentComplete: number | null;
  committed: boolean;
  ranges: { annual: string; fullRange: string; netValue3Yr: string; payback: string; conservativePayback: string };
  onGoTab: (tab: 'approval' | 'vendors' | 'execution') => void;
  onOpenBrief: () => void;
  activity?: ActivityEvent[];
}

export const ExecSummaryTab: React.FC<ExecSummaryTabProps> = ({
  brief, record, scores, milestones, approvalState, nextAction, percentComplete, committed, ranges, onGoTab, onOpenBrief, activity = [],
}) => {
  const severityOrder = ['Critical', 'High', 'Moderate', 'Low'];
  const topRisks = [...record.risks]
    .filter((r) => r.status === 'Open')
    .sort((a, b) => severityOrder.indexOf(a.severity) - severityOrder.indexOf(b.severity))
    .slice(0, 3);
  const nextMilestones = milestones.filter((m) => m.status !== 'Complete').slice(0, 3);

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-4 items-start">
      <div className="space-y-4">
        {/* Next required action leads: the one thing someone has to do. */}
        <div className="rounded-xl border border-[#FF6E4C]/40 bg-[#FFF1ED] px-4 py-3 flex items-center gap-3">
          <Flag className="w-4 h-4 text-[#FF6E4C] shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold text-[#FF6E4C] uppercase tracking-wide">Next required action</p>
            <p className="text-sm font-semibold text-[#18365E]">{nextAction.text}</p>
          </div>
          <button onClick={() => onGoTab(nextAction.tab)} className="text-xs font-semibold text-[#18365E] hover:text-[#FF6E4C] flex items-center gap-1 shrink-0">
            {nextAction.tab === 'approval' ? 'Approval' : 'Use case execution'} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        <SectionCard icon={<Target className="w-3.5 h-3.5" />} title="Status">
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
            {[
              ['Status', record.status],
              ['Approval', approvalState],
              ['Initiative owner', record.owner],
              ['Executive sponsor', record.sponsor],
              ['Current phase', record.phase],
              ['Timeline', record.start === '—' || record.target === '—' || /pending/i.test(record.start) ? `${brief.ttv} after approval` : `${record.start} to ${record.target}`],
              ['Percent complete', percentComplete === null ? 'Not reported' : `${percentComplete}%`],
              ['Client commitment', committed ? 'Committed' : 'Not committed'],
            ].map(([k, v]) => (
              <div key={k}>
                <dt className="text-[10px] text-[#717182] font-semibold uppercase tracking-wide">{k}</dt>
                <dd className="text-xs font-bold text-[#18365E] mt-0.5">{v}</dd>
              </div>
            ))}
          </dl>
        </SectionCard>

        <SectionCard icon={<Award className="w-3.5 h-3.5" />} title="Evaluation result">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <VerdictPill verdict={brief.verdict} />
            <span className="text-xs text-[#4b5563]">Composite <b className="text-[#18365E]">{scores.total} / 100</b></span>
            <button onClick={onOpenBrief} className="ml-auto text-xs font-semibold text-[#18365E] hover:text-[#FF6E4C] flex items-center gap-1">
              Evaluation brief <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            {scores.dims.map((d) => (
              <div key={d.id} className="rounded-lg border border-[#e5e7eb] bg-white px-3 py-2">
                <p className="text-[10px] text-[#717182] font-semibold">{DIM_META.find((m) => m.id === d.id)?.name ?? d.id}</p>
                <p className="text-base font-bold text-[#18365E]">{Math.round(d.score)}</p>
                <div className="h-1 rounded-full bg-[#e5e7eb] mt-1"><div className="h-1 rounded-full bg-[#18365E]" style={{ width: `${Math.max(0, Math.min(100, d.score))}%` }} /></div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-[#4b5563] mt-3">
            Annual value {ranges.fullRange} (conservative to aggressive) · base case {ranges.annual} · net 3-year value {ranges.netValue3Yr} · payback {ranges.payback} base, {ranges.conservativePayback} conservative.
          </p>
        </SectionCard>

        <SectionCard icon={<Shield className="w-3.5 h-3.5" />} title="Top open risks">
          {topRisks.length === 0 ? (
            <p className="text-xs text-[#717182]">No open risks logged.</p>
          ) : (
            <ul className="space-y-2.5">
              {topRisks.map((r) => (
                <li key={r.title} className="text-xs">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${SEVERITY_DOT[r.severity] ?? 'bg-slate-400'}`} />
                    <span className="font-bold text-[#18365E]">{r.title}</span>
                    <span className="text-[10px] text-[#717182]">{r.severity} · {r.likelihood} likelihood</span>
                  </div>
                  <p className="text-[#4b5563] mt-0.5 ml-4"><b className="text-[#18365E]">Mitigation:</b> {r.mitigation}</p>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>

      <div className="space-y-4">
        <SectionCard icon={<Calendar className="w-3.5 h-3.5" />} title="Next milestones" accent>
          {nextMilestones.length === 0 ? (
            <p className="text-xs text-[#717182]">All milestones complete.</p>
          ) : (
            <ul className="space-y-2.5">
              {nextMilestones.map((m) => (
                <li key={m.title} className="text-xs">
                  <p className="font-semibold text-[#18365E]">{m.title}</p>
                  <p className="text-[10px] text-[#717182]">{m.phase} · {m.owner} · {m.due} · {m.status}</p>
                </li>
              ))}
            </ul>
          )}
          <button onClick={() => onGoTab('execution')} className="mt-3 text-xs font-semibold text-[#18365E] hover:text-[#FF6E4C] flex items-center gap-1">
            Use case execution <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </SectionCard>

        <SectionCard icon={<Flag className="w-3.5 h-3.5" />} title="Latest activity">
          <ActivityLog events={activity} limit={4} />
          {activity.length > 4 && (
            <button onClick={() => onGoTab('approval')} className="mt-3 text-xs font-semibold text-[#18365E] hover:text-[#FF6E4C] flex items-center gap-1">
              All activity <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </SectionCard>

        <SectionCard icon={<Target className="w-3.5 h-3.5" />} title="Expected outcomes">
          <ul className="space-y-2">
            {record.outcomes.map((o) => (
              <li key={o} className="flex items-start gap-2 text-xs text-[#4b5563] leading-relaxed">
                <ArrowRight className="w-3.5 h-3.5 text-[#FF6E4C] mt-0.5 shrink-0" />{o}
              </li>
            ))}
          </ul>
        </SectionCard>
      </div>
    </div>
  );
};

export default ExecSummaryTab;
